import React, { createContext, useContext, useReducer, ReactNode } from 'react';

interface PaymentState {
  isProcessing: boolean;
  paymentMethod: 'card' | 'bank' | null;
  paymentData: any;
  error: string | null;
  success: boolean;
}

interface PaymentAction {
  type: 'SET_PROCESSING' | 'SET_PAYMENT_METHOD' | 'SET_PAYMENT_DATA' | 'SET_ERROR' | 'SET_SUCCESS' | 'RESET';
  payload?: any;
}

const initialState: PaymentState = {
  isProcessing: false,
  paymentMethod: null,
  paymentData: null,
  error: null,
  success: false,
};

const paymentReducer = (state: PaymentState, action: PaymentAction): PaymentState => {
  switch (action.type) {
    case 'SET_PROCESSING':
      return { ...state, isProcessing: action.payload, error: null };
    case 'SET_PAYMENT_METHOD':
      return { ...state, paymentMethod: action.payload };
    case 'SET_PAYMENT_DATA':
      return { ...state, paymentData: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isProcessing: false };
    case 'SET_SUCCESS':
      return { ...state, success: action.payload, isProcessing: false, error: null };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

interface PaymentContextType {
  state: PaymentState;
  dispatch: React.Dispatch<PaymentAction>;
  processPayment: (eventId: string, customerData: any, paymentData: any) => Promise<void>;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

interface PaymentProviderProps {
  children: ReactNode;
}

export const PaymentProvider: React.FC<PaymentProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(paymentReducer, initialState);

  // Xplor API integration
  const processPayment = async (eventId: string, customerData: any, paymentData: any) => {
    dispatch({ type: 'SET_PROCESSING', payload: true });

    try {
      // This would be your actual Xplor API call
      const response = await fetch('/api/payments/process', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_XPLOR_API_KEY}`,
        },
        body: JSON.stringify({
          eventId,
          customer: customerData,
          payment: paymentData,
          // Add Xplor-specific fields as needed
        }),
      });

      if (!response.ok) {
        throw new Error('Payment processing failed');
      }

      const result = await response.json();
      
      dispatch({ type: 'SET_SUCCESS', payload: true });
      dispatch({ type: 'SET_PAYMENT_DATA', payload: result });

    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Payment failed' 
      });
    }
  };

  const value = {
    state,
    dispatch,
    processPayment,
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export const usePayment = (): PaymentContextType => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};